/*
 * Double Promo — video pause/play controls and viewport-based auto-pause.
 * Scoped per [data-double-promo] section root.
 * Hooks: [data-media-card], [data-video-toggle].
 */

class DoublePromo {
  constructor(root) {
    this.root = root;
    this.initVideoControls();
    this.initViewportPause();
  }

  initVideoControls() {
    this.root.querySelectorAll('[data-video-toggle]').forEach((btn) => {
      const video = btn.closest('[data-media-card]')?.querySelector('video');
      if (!video) return;

      video.controls = false;
      const update = () => this.setVideoState(btn, video);

      video.addEventListener('play', () => {
        if (video.dataset.inViewport === 'false' && video.dataset.userPaused !== 'true') {
          video.dataset.autoPaused = 'true';
          video.pause();
          return;
        }
        update();
      });

      video.addEventListener('pause', update);

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        if (video.paused) {
          delete video.dataset.userPaused;
          delete video.dataset.autoPaused;
          video.play().catch(() => update());
        } else {
          video.dataset.userPaused = 'true';
          delete video.dataset.autoPaused;
          video.pause();
        }
      });

      // Seed optimistic: assume the video will autoplay, so the discrete-mode
      // button stays hidden. Only flip to paused if autoplay is genuinely blocked.
      btn.dataset.videoState = 'playing';
      btn.setAttribute('aria-label', 'Pause video');
      btn.setAttribute('aria-pressed', 'true');

      const playProbe = video.play();
      if (playProbe && typeof playProbe.then === 'function') {
        // An AbortError is our own pause() interrupting the probe; only a refusal means paused.
        playProbe.catch((err) => {
          if (err && err.name === 'AbortError') return;
          this.setVideoState(btn, video, { force: true });
        });
      }
    });
  }

  initViewportPause() {
    const videos = this.root.querySelectorAll('video');
    if (!videos.length) return;

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      videos.forEach((video) => {
        video.dataset.userPaused = 'true';
        video.pause();
        const btn = video.closest('[data-media-card]')?.querySelector('[data-video-toggle]');
        if (btn) this.setVideoState(btn, video, { force: true });
      });
      return;
    }

    if (!('IntersectionObserver' in window)) return;

    this.viewportObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          video.dataset.inViewport = entry.isIntersecting ? 'true' : 'false';

          if (entry.isIntersecting) {
            if (video.dataset.autoPaused === 'true' && video.dataset.userPaused !== 'true') {
              delete video.dataset.autoPaused;
              video.play().catch(() => {
                video.dataset.autoPaused = 'true';
              });
            }
            return;
          }

          if (!video.paused && video.dataset.userPaused !== 'true') {
            video.dataset.autoPaused = 'true';
            video.pause();
          }
        });
      },
      { threshold: 0.1 },
    );

    videos.forEach((video) => this.viewportObserver.observe(video));
  }

  setVideoState(btn, video, options) {
    // A pause before playback has begun is start-up noise from our own viewport/breakpoint handling,
    // not a user action. `force` marks the cases that must still show the button.
    if (!(options && options.force) && video.paused && video.played.length === 0) return;
    const isPaused = video.paused;
    // TODO(i18n): localize 'Play video' / 'Pause video' aria-label.
    btn.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
    btn.setAttribute('aria-pressed', String(!isPaused));
    btn.dataset.videoState = isPaused ? 'paused' : 'playing';
  }
}

function initSections(container = document) {
  const selector = '[data-double-promo]:not([data-double-promo-init])';
  const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);

  roots.forEach((root) => {
    root.dataset.doublePromoInit = 'true';
    new DoublePromo(root);
  });
}

initSections();

document.addEventListener('shopify:section:load', (event) => initSections(event.target));
